import { BookOpenTextIcon, CalendarCheckIcon, CalendarXIcon, FlaskConicalIcon, ClipboardCheckIcon, FileTextIcon, FolderSearchIcon, LayersIcon, LayoutGridIcon, TargetIcon, UsersIcon } from 'lucide-react';
import type { LucideIcon } from 'lucide-react';
// NATURFAG KLAR™ (Norge). File keeps its original name so every component import stays untouched.
export const PRODUCT_IMAGE = 'https://aekpgsbqjzqntlbucbts.supabase.co/storage/v1/object/public/store-resources/94f2694a-3721-4e47-98b7-e7959cfe1f77/1792637884655_naturfagklar-mockup-6-bind-transparente-04419815.webp';
export const heroGuarantees = ['Umiddelbar digital nedlasting', 'Engangsbetaling', 'Ingen abonnement', '7 dagers fornøydgaranti'];
export const stats = [{
  value: '240',
  label: 'Sider i hovedbindene'
}, {
  value: '6',
  label: 'Digitale bind'
}, {
  value: '20',
  label: 'Trygge forsøk'
}, {
  value: '5',
  label: 'Bonuser inkludert'
}, {
  value: '3',
  label: 'Nivåer på arbeidsarkene'
}];
export const differenceStats = [{
  value: '1.–10. trinn',
  label: 'Fra barneskole til ungdomsskole'
}, {
  value: '6 digitale bind',
  label: 'Fra sansene og vannets kretsløp til celler, kjemiske reaksjoner og Ohms lov'
}, {
  value: 'PDF klar til utskrift',
  label: 'Bruk det digitalt eller skriv ut til naturfagrommet'
}];
export const beforeItems: Array<{
  title: string;
  text: string;
}> = [{
  title: 'Kvelder og helger forsvinner',
  text: 'Timer foran skjermen for å planlegge neste ukes naturfagtimer – og du er fortsatt ikke ferdig.'
}, {
  title: 'Forsøk som må finnes opp på nytt',
  text: 'Du leter etter forsøk som er trygge, enkle og fungerer med det materiellet skolen faktisk har.'
}, {
  title: 'Klasser med store nivåforskjeller',
  text: 'Noen elever trenger støtte for å forstå fagbegrepene, andre er ferdige på fem minutter – og timen skal fungere for alle.'
}, {
  title: 'Forsøkstimer som sporer av',
  text: 'Utstyr overalt, grupper som ikke vet hva de skal gjøre, og ti minutter går med til å rydde.'
}, {
  title: 'Vanskelig å vurdere og dokumentere',
  text: 'Kompetansemål, underveisvurdering og standpunkt – hvordan begrunner og dokumenterer du det?'
}, {
  title: 'Stress når timen begynner',
  text: 'Når tiden ikke strekker til, blir det fort «les side 40 til 45 i boka» og improvisasjon i siste liten.'
}];
export const afterItems = ['En ferdig årsplan for 1.–10. trinn', '12 ferdige økter minutt for minutt', 'Materiell, sikkerhet og lærerinstruks til hver økt', 'Arbeidsark i 3 nivåer', '20 trygge forsøk med hverdagsmateriell', 'Fasit til alt', 'Koblet til kompetansemålene i LK20', 'Prøver, vurderingsskjema og ferdige tilbakemeldinger', 'PDF klar til utskrift'];
export const recognizeItems: Array<{
  icon: LucideIcon;
  title: string;
  text: string;
}> = [{
  icon: CalendarXIcon,
  title: 'Kveldene går med til planlegging',
  text: 'Bruker du kvelder og helger på å planlegge neste ukes naturfagtimer?'
}, {
  icon: FolderSearchIcon,
  title: 'Du leter etter forsøk som fungerer',
  text: 'Leter du hele tiden på nettet etter forsøk som er trygge og passer trinnet og kompetansemålene?'
}, {
  icon: UsersIcon,
  title: 'Klassen har store nivåforskjeller',
  text: 'Trenger du oppgaver som fungerer både for elever som trenger støtte og for dem som vil ha utfordringer?'
}, {
  icon: TargetIcon,
  title: 'Fagbegrepene sitter ikke',
  text: 'Ser du elever som blander fordamping og kondensasjon, eller cellevegg og cellemembran – men har ikke tid til å lage ekstra opplegg?'
}, {
  icon: ClipboardCheckIcon,
  title: 'Vurderingen tar tid',
  text: 'Vil du ha ferdige prøver, vurderingsskjema og tilbakemeldinger klare til hvert tema?'
}, {
  icon: LayoutGridIcon,
  title: 'Du trenger en tydeligere struktur',
  text: 'Vil du ha alt samlet, slik at du raskt finner riktig økt når timen begynner?'
}];

// Spine colours mirror the physical box (blue, light blue, green, yellow, orange, red).
export const volumes: Array<{
  number: string;
  level: string;
  title: string;
  tagline: string;
  intro: string;
  items: string[];
  outro: string;
  note?: string;
  spine: string;
  rule: string;
}> = [{
  number: 'Bind 01',
  level: '1.–10. trinn',
  title: 'Årsplan + 12 økter minutt for minutt',
  tagline: 'Vit nøyaktig hva hver naturfagtime skal handle om – hele skoleåret.',
  intro: 'Det første bindet gir deg en komplett årsplan for 1.–4., 5.–7. og 8.–10. trinn og tolv ferdige økter som du kan gjennomføre med en gang.',
  items: ['Årsplan for 1.–4., 5.–7. og 8.–10. trinn', 'Høsthalvåret uke for uke', '12 ferdige økter minutt for minutt (60 min)', 'Materiell, sikkerhet og lærerinstruks til hver økt', 'Sikkerhetsregler og sikkerhetskontrakt', 'Elevark i 3 nivåer med fasit', 'Prøver for 1.–7. og 8.–10. trinn', 'Vurderingskriterier'],
  outro: 'Ferdige økter som gjør at du går inn i naturfagtimen med en tydelig plan.',
  spine: 'bg-blue-700 text-white',
  rule: 'border-white/30'
}, {
  number: 'Bind 02',
  level: '1.–10. trinn',
  title: 'Jorda og livet på jorda + Kropp og helse',
  tagline: 'Planter, dyr, økosystemer og kroppen – gjort konkret.',
  intro: 'Det andre bindet tar elevene med fra frø og småkryp til celler, næringskjeder, kroppens organer og bærekraft.',
  items: ['Planter og fotosyntesen', 'Dyr og leveområder', 'Næringskjeder og økosystemer', 'Cellen', 'Arv og evolusjon', 'Kroppens organer, helse og livsstil', 'Bærekraftig utvikling', 'Arbeidsark i 3 nivåer og fasit'],
  outro: 'Biologi elevene kan se, ta og undersøke selv.',
  spine: 'bg-sky-500 text-slate-900',
  rule: 'border-slate-900/20'
}, {
  number: 'Bind 03',
  level: '1.–10. trinn',
  title: 'Energi og materie: fysikk',
  tagline: 'Krefter, lys, lyd og elektrisitet – forklart så alle forstår.',
  intro: 'Det tredje bindet gjør fysikken håndgripelig med enkle målinger, demonstrasjoner og hverdagseksempler.',
  items: ['Krefter og bevegelse', 'Energi og energiformer', 'Lys og skygge', 'Lyd', 'Elektrisitet og strømkretser', 'Magnetisme', 'Verdensrommet og solsystemet', 'Vær og klima'],
  outro: 'Tydelige økter som gjør fysikk forståelig for hele klassen.',
  spine: 'bg-green-600 text-white',
  rule: 'border-white/30'
}, {
  number: 'Bind 04',
  level: '1.–10. trinn',
  title: 'Energi og materie: kjemi',
  tagline: 'Fra blandinger og vann til reaksjoner og periodesystemet.',
  intro: 'Det fjerde bindet gir elevene stoffene og reaksjonene rundt seg – trygt og konkret.',
  items: ['Stoffer og blandinger', 'Vann og tilstandsformer', 'Luft', 'Kjemiske reaksjoner', 'Syrer og baser', 'Periodesystemet', 'Kjemi i hverdagen', 'Arbeidsark i 3 nivåer og fasit'],
  outro: 'Kjemi som elevene kjenner igjen fra kjøkkenet og naturen.',
  spine: 'bg-yellow-400 text-slate-900',
  rule: 'border-slate-900/20'
}, {
  number: 'Bind 05',
  level: '1.–10. trinn',
  title: 'Forsøk, utforsking og teknologi',
  tagline: '20 trygge forsøk – steg for steg.',
  intro: 'Det femte bindet samler forsøkene med hverdagsmateriell, slik at elevene trener naturvitenskapelige praksiser og tenkemåter.',
  items: ['20 trygge forsøk med hverdagsmateriell', 'Materiell, steg og sikkerhet til hvert forsøk', 'Hypotese – forsøk – resultat – konklusjon', 'Maler for forsøksrapport', 'Sikkerhetsregler', 'Teknologi- og designoppdrag'],
  outro: 'Forsøkstimer som fungerer – uten å finne opp alt på nytt.',
  spine: 'bg-orange-500 text-slate-900',
  rule: 'border-slate-900/20'
}, {
  number: 'Bind 06',
  level: '1.–10. trinn',
  title: 'Vurdering',
  tagline: 'Vis hva elevene kan – rettferdig og dokumentert.',
  intro: 'Det sjette bindet samler verktøyene for underveisvurdering og sluttvurdering i naturfag.',
  items: ['Prøver A/B per tema med fasit', 'Vurderingsskjema i 3 nivåer', 'Egenvurdering for elevene', 'Underveisvurdering', '100 ferdige tilbakemeldinger'],
  outro: 'En komplett støtte for å vurdere rettferdig og spare tid.',
  note: 'Materialet er selvstendig utviklet og er ikke et offisielt produkt fra Utdanningsdirektoratet.',
  spine: 'bg-red-600 text-white',
  rule: 'border-white/30'
}];
export const volumesIntro = {
  title: '6 digitale bind i naturfag',
  text: 'Et komplett undervisningssystem som hjelper deg å planlegge og gjennomføre naturfaget hele skoleåret. Hvert bind har 40 sider med ferdige økter, forsøk, arbeidsark i 3 nivåer og fasit.'
};
export const packItems: Array<{
  icon: LucideIcon;
  title: string;
  text: string;
}> = [{
  icon: BookOpenTextIcon,
  title: 'Ferdige økter minutt for minutt',
  text: 'Slipp å begynne på et tomt ark. Tydelige opplegg med tider, materiell, sikkerhet og lærerinstruks hjelper deg å komme raskt i gang.'
}, {
  icon: FlaskConicalIcon,
  title: '20 trygge forsøk',
  text: 'Forsøk med hverdagsmateriell, steg for steg, med sikkerhetsregler og forklaring på hva som skjer.'
}, {
  icon: FileTextIcon,
  title: 'Arbeidsark i 3 nivåer',
  text: 'Samme mål i tre nivåer – støtte, basis og utfordring – slik at alle elever kan jobbe med det samme.'
}, {
  icon: LayersIcon,
  title: 'Koblet til LK20',
  text: 'Kjerneelementene og kompetansemålene i naturfag for 1.–10. trinn – tydelig koblet til øktene.'
}, {
  icon: ClipboardCheckIcon,
  title: 'Vurdering med fasit',
  text: 'Prøver A/B, vurderingsskjema, egenvurdering og 100 ferdige tilbakemeldinger.'
}, {
  icon: CalendarCheckIcon,
  title: 'Årsplan',
  text: 'Støtte for å organisere hele skoleåret og velge temaer ut fra trinnet og klassen.'
}];
// Real pages from Bind 1 made in Gamma Studio. While empty, InsidePages shows a neutral placeholder.
const PAGE_BASE = 'https://aekpgsbqjzqntlbucbts.supabase.co/storage/v1/object/public/store-resources/94f2694a-3721-4e47-98b7-e7959cfe1f77/';
export const pageSamples: Array<{
  src: string;
  label: string;
  title: string;
  text: string;
}> = [{
  src: `${PAGE_BASE}1792637822801_nk-bind1-side01-8ce2484f.webp`,
  label: 'Bind 1 · Side 1',
  title: 'Årsplan + 12 økter minutt for minutt',
  text: 'Hvert bind starter med en tydelig oversikt over temaene og hva som er inkludert.'
}, {
  src: `${PAGE_BASE}1792637826479_nk-bind1-side05-aafc2cfc.webp`,
  label: 'Bind 1 · Side 5',
  title: 'Årsplan 1.–4. trinn – 38 skoleuker',
  text: 'Hele skoleåret uke for uke, med tema, nøkkelaktivitet og hvilke økter som hører til.'
}, {
  src: `${PAGE_BASE}1792637830556_nk-bind1-side32-c08bea42.webp`,
  label: 'Bind 1 · Side 32',
  title: 'Økt 11: Syrer og baser med rødkålindikator',
  text: 'En ferdig økt på 60 minutter: mål, materiell, sikkerhet, forsøket steg for steg og lærerinstruks.'
}, {
  src: `${PAGE_BASE}1792637834287_nk-bind1-side33-4686b1c5.webp`,
  label: 'Bind 1 · Side 33',
  title: 'Elevark: Syrer og baser',
  text: 'Samme mål i tre nivåer – støtte, basis og utfordring – med fasit til læreren.'
}, {
  src: `${PAGE_BASE}1792637838142_nk-bind1-side38-f4ca8449.webp`,
  label: 'Bind 1 · Side 38',
  title: 'Fasit til prøvene',
  text: 'Fasit til prøvene for 1.–7. og 8.–10. trinn, slik at du sparer tid på retting.'
}];
export const pageTypes = ['Ferdige økter', 'Forsøk', 'Arbeidsark i 3 nivåer', 'Fasit'];
export const bonuses: Array<{
  title: string;
  text: string;
  items: string[];
  benefit: string;
}> = [{
  title: 'Naturfagstart på 5 minutter',
  text: '40 korte oppstarter som vekker nysgjerrigheten i starten av timen.',
  items: ['10 gåter', '10 «Hva skjer hvis …?»', '10 bildespørsmål', '10 minidemonstrasjoner'],
  benefit: 'Hele klassen med fra første minutt.'
}, {
  title: 'Ute-naturfag: 30 ferdige uteøkter',
  text: 'Naturfag ute i skolegården og i skogen – ferdig planlagt.',
  items: ['30 uteøkter', 'Artskort med norske arter', 'Værstasjon og skyggemåling', 'Elevark med fasit'],
  benefit: 'Bruk naturen rett utenfor døra.'
}, {
  title: 'Foreldrekommunikasjon i naturfag',
  text: 'Ferdige brev til hjemmet, klare til å fylle ut og sende.',
  items: ['Velkomstbrev', 'Info om forsøk og turdager', 'Samtykke og ukebrev', 'Mal for utviklingssamtale'],
  benefit: 'Trygge foreldre – mindre e-post.'
}, {
  title: 'Vikarpermen for naturfag',
  text: '15 naturfagøkter som en vikar kan gjennomføre uten forberedelse.',
  items: ['15 ferdige økter', 'Brev til vikaren', 'Elevark med fasit', 'Klar til utskrift'],
  benefit: 'Ingen bortkastede timer når du er borte.'
}, {
  title: 'Plakater for naturfagrommet',
  text: '20 fargerike plakater til veggen i naturfagrommet.',
  items: ['Sikkerhetsregler', 'Periodesystemet for barn', 'Vannets kretsløp og planetene', 'Ordliste med fagord'],
  benefit: 'Et naturfagrom som lærer bort av seg selv.'
}];
export const steps: Array<{
  title: string;
  text: string;
}> = [{
  title: 'Åpne NATURFAG KLAR™',
  text: 'Last ned pakken og åpne bindet du trenger.'
}, {
  title: 'Velg trinn og økt',
  text: 'Finn riktig økt, forsøk eller arbeidsark for klassen din.'
}, {
  title: 'Forbered deg på 5 minutter',
  text: 'Les gjennom lærerinstruksen og gjør klart materiellet du trenger.'
}, {
  title: 'Skriv ut og start timen',
  text: 'Skriv ut arbeidsarkene eller bruk materialet digitalt – og gå inn i naturfagtimen med en tydelig plan.'
}];
export const audienceItems = ['Underviser i naturfag på 1.–10. trinn', 'Er kontaktlærer og har naturfag på timeplanen', 'Vil ha en ferdig årsplan koblet til LK20', 'Vil ha trygge forsøk som faktisk fungerer', 'Har klasser med store nivåforskjeller', 'Vil slippe å planlegge hver time fra bunnen av', 'Vil ha arbeidsark i 3 nivåer med fasit', 'Vil spare kvelder og helger på planlegging'];
export const timeValueTasks = ['Lete etter forsøk på nettet', 'Planlegge øktene', 'Lage arbeidsark i flere nivåer', 'Lage fasit og prøver', 'Vurdere og gi tilbakemeldinger'];
export const offerRows: Array<{
  label: string;
  detail: string;
}> = [{
  label: '6 digitale bind – 240 sider',
  detail: 'Årsplan og økter, jorda og livet på jorda, kropp og helse, fysikk, kjemi, forsøk og vurdering'
}, {
  label: '5 bonuser uten ekstra kostnad',
  detail: 'Naturfagstart, Ute-naturfag, Foreldrekommunikasjon, Vikarpermen og Plakater for naturfagrommet'
}, {
  label: 'For 1.–10. trinn',
  detail: 'Ferdige økter, 20 trygge forsøk, arbeidsark i 3 nivåer, fasit og vurderingsskjema'
}];
export const offerIncludes = ['Umiddelbar digital nedlasting', 'PDF-filer du kan lagre', 'Klar til utskrift', 'Ingen fysisk vare sendes'];
export const PRICE_AMOUNT = '299';
export const CURRENCY = 'NOK';
export const PRICE = `${PRICE_AMOUNT}\u00A0${CURRENCY}`;
export const PRICE_BEFORE = '599\u00A0NOK';

// Only add authentic reviews of NATURFAG KLAR. The section stays hidden while empty.
export const testimonials: Array<{
  quote: string;
  name: string;
  location: string;
}> = [];
export const faqs = [{
  q: 'Hvordan får jeg materialet?',
  a: 'NATURFAG KLAR™ leveres digitalt. Etter kjøpet får du tilgang til PDF-filer som du kan laste ned og lagre. Du kan bruke materialet på PC, nettbrett eller mobil og skrive ut de sidene du trenger.'
}, {
  q: 'Er det tilpasset LK20?',
  a: 'Ja. Årsplanen og øktene er koblet til kjerneelementene og kompetansemålene i naturfag for 1.–10. trinn. NATURFAG KLAR™ er et selvstendig undervisningsmateriell og ikke et offisielt produkt fra Utdanningsdirektoratet.'
}, {
  q: 'Hvilke trinn passer det for?',
  a: 'Materialet dekker 1.–10. trinn. Årsplanen i Bind 1 har egne planer for 1.–4., 5.–7. og 8.–10. trinn, og hvert tema i Bind 2–6 har opplegg som du velger ut fra trinnet og klassen din.'
}, {
  q: 'Trenger jeg spesielt utstyr til forsøkene?',
  a: 'Nei. Forsøkene bruker hverdagsmateriell som vann, glass, salt, sukker, rødkål, magneter, lommelykt og batterier. Hvert forsøk har en materielliste og sikkerhetsregler.'
}, {
  q: 'Kan jeg virkelig forberede en økt på 5 minutter?',
  a: 'NATURFAG KLAR™ er laget for å gjøre forberedelsene raskere. Du åpner et bind, velger en ferdig økt og leser gjennom den på omtrent fem minutter. Tiden kan variere ut fra økta og behovet for tilpasning.'
}, {
  q: 'Er det arbeidsark i flere nivåer?',
  a: 'Ja. Arbeidsarkene finnes i 3 nivåer – støtte, basis og utfordring – slik at elever med ulike behov kan jobbe med samme mål.'
}, {
  q: 'Er det fasit?',
  a: 'Ja. Det er fasit til arbeidsarkene, oppgavene og prøvene, slik at du sparer tid på retting.'
}, {
  q: 'Er vurdering inkludert?',
  a: 'Ja. Bind 6 inneholder prøver A/B med fasit, vurderingsskjema i 3 nivåer, egenvurdering, underveisvurdering og 100 ferdige tilbakemeldinger.'
}, {
  q: 'Må jeg skrive ut alt?',
  a: 'Nei. Du velger selv hvilke sider du skriver ut, og du kan også bruke deler av materialet digitalt.'
}, {
  q: 'Er de fem bonusene inkludert?',
  a: `Ja. Alle fem bonusene er inkludert i pakken uten ekstra kostnad – alt for ${PRICE}.`
}, {
  q: 'Er det et abonnement?',
  a: 'Nei. Du betaler én gang og får tilgang til det digitale materialet. Ingen faste trekk.'
}, {
  q: 'Finnes det en garanti?',
  a: 'Ja. NATURFAG KLAR™ har 7 dagers fornøydgaranti. Hvis materialet ikke svarer til forventningene dine, kan du kontakte oss og be om å få pengene tilbake innen garantiperioden, i henhold til vilkårene.'
}];
export const footerLinks = ['Kjøpsvilkår', 'Personvern', 'Refusjon', 'Kontakt'];
